import { inject, Injectable } from '@angular/core';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AnalyticsKeyEnum } from '@app/core/enums/analytics/analytics-key.enum';
import { ActionEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { AnalyticsEvent } from '@app/core/model/entities/analytics/analytics-event';
import { Chart, ChartOptions, Dashboard } from '@app/core/model/entities/dashboard/dashboard';
import {
  CreateDashboardModalComponent
} from '@app/shared/components/dashboard/create-dashboard-modal/create-dashboard-modal.component';
import { setValue } from '@app/shared/extra/utils';
import { FieldFormatTypePipe } from '@app/shared/pipes/field-format-type.pipe';
import { TranslateService } from '@ngx-translate/core';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { PopupManager, PopupSize } from '@services/managers/popup.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import {
  AgChartLabelFormatterParams,
  AgSeriesTooltipRendererParams,
  AgTooltipRendererResult
} from 'ag-charts-community';
import { ChartModel, ChartType, CreateRangeChartParams } from 'ag-grid-community';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { DocumentNode } from 'graphql';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';


@Injectable()
export class DashboardService {
  private readonly apiService = inject(ApiService);
  private readonly appManager = inject(AppManager);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly popupManager = inject(PopupManager);
  private readonly snackbarManager = inject(SnackbarManager);
  private readonly translate = inject(TranslateService);
  private readonly fieldFormatPipe = inject(FieldFormatTypePipe);

  public currentDashboard: Dashboard;
  public sidePanelToggleSubject: BehaviorSubject<Dashboard[]> = new BehaviorSubject(null);
  public chartModels: ChartModel[];

  private applyDashboardSubject = new Subject<Dashboard>();
  private changeDashboardSubject = new Subject<Dashboard>();
  private updateDashboardSubject = new Subject<Dashboard>();
  private deleteDashboardSubject = new Subject<Dashboard>();
  private readonly dashboardGraphqlFragment: DocumentNode = gql`
    fragment DashboardInfo on Dashboard {
      id
      name
      entityType
      isDefault
      chartsList
    }
  `;

  /**
   * Emits a new Dashboard to apply as the current one after its creation.
   */
  public get dashboardApplied$(): Observable<Dashboard> {
    return this.applyDashboardSubject.asObservable();
  }

  /**
   * Emits the previous Dashboard applied after a change of current dashboard.
   */
  public get dashboardReplaced$(): Observable<Dashboard> {
    return this.changeDashboardSubject.asObservable();
  }

  /**
   * Emits a Dashboard whenever it has been updated.
   */
  public get dashboardUpdated$(): Observable<Dashboard> {
    return this.updateDashboardSubject.asObservable();
  }

  /**
   * Emits a Dashboard whenever it has been deleted.
   */
  public get dashboardDeleted$(): Observable<Dashboard> {
    return this.deleteDashboardSubject.asObservable();
  }

  /**
   * Replace current dashboard by a new one.
   * @param dashboard New current dashboard
   */
  public changeDashboard(dashboard: Dashboard): void {
    const previousDashboard = this.currentDashboard;
    this.currentDashboard = dashboard;
    this.currentDashboard.isModified = false;
    this.changeDashboardSubject.next(previousDashboard);
  }

  /**
   * Make an API request to fetch Dashboards linked to the current Organization and to an entity type specified in param.
   * @param entityType Type of the entity which is associated to the dashboards
   * @return Observable emitting a list of all Dashboards.
   */
  public loadDashboards(entityType: EntityTypeEnum): Observable<Dashboard[]> {
    const query = gql`
      query DashboardsByOrganizationIdAndEntityType($organizationId: String!, $entityType: EntityType!) {
        dashboardsByOrganizationIdAndEntityType(organizationId: $organizationId, entityType: $entityType) {
          ...DashboardInfo
        }
      }
      ${this.dashboardGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      entityType
    };
    return this.apiService.query({query, variables})
      .pipe(
        map(data => plainToInstance(Dashboard, data['dashboardsByOrganizationIdAndEntityType'] as Dashboard[]))
      );
  }

  /**
   * Return a copy of the current dashboard with the current options and configs found in chart models.
   * @return {Dashboard} Copy of current dashboard updated with the current chart models
   * @private
   */
  private copyCurrentDashboard(): Dashboard {
    const copyDashboard = Object.assign({}, this.currentDashboard);
    copyDashboard.charts = copyDashboard.charts.map(chart => {
      const copy = Object.assign({}, chart);
      copy.options = Object.assign({}, copy.options);
      copy.config = undefined;
      return copy;
    });
    this.chartModels.forEach(chartModel => {
      const index = copyDashboard.charts.findIndex(chart => chart.chartRef.chartId == chartModel.chartId);
      if (index >= 0) {
        const themeOverrides = Object.assign(
          this.currentDashboard.charts[index].config.chartThemeOverrides,
          chartModel.chartOptions
        );
        copyDashboard.charts[index].config = <CreateRangeChartParams>{
          chartType: chartModel.chartType,
          cellRange: chartModel.cellRange,
          aggFunc: chartModel.aggFunc,
          chartThemeName: chartModel.chartThemeName,
          chartThemeOverrides: themeOverrides
        };
      }
    });

    this.currentDashboard.isModified = false;
    return copyDashboard;
  }

  /**
   * Save current dashboard state and call the API to update it.
   * @return Observable emitting the updated Dashboard once the update is completed
   */
  public updateDashboard(): Observable<Dashboard> {
    this.currentDashboard.charts = this.copyCurrentDashboard().charts;
    const mutation = gql`
      mutation UpdateDashboard($dashboardId: String!, $dashboardInput: DashboardInput!) {
        updateDashboard(dashboardId: $dashboardId, updateDashboardInput: $dashboardInput){
          ...DashboardInfo
        }
      }
      ${this.dashboardGraphqlFragment}
    `;
    const variables = {
      dashboardId: this.currentDashboard.id,
      dashboardInput: {
        name: this.currentDashboard.name,
        charts: this.currentDashboard.charts.map(chart => {
          return {
            options: chart.options,
            config: chart.config
          };
        })
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => {
          this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.DASHBOARD_UPDATED'));
          const dashboard = plainToInstance(Dashboard, data['updateDashboard']);
          this.updateDashboardSubject.next(dashboard);
          return dashboard;
        })
      );
  }

  /**
   * Open dialog to create a dashboard
   */
  public openCreateDashboardDialog(): void {
    const dialogRef = this.popupManager.showGenericPopup(
      CreateDashboardModalComponent,
      PopupSize.SMALL,
      this.currentDashboard.entityType
    );
    dialogRef.afterClosed()
      .pipe(
        switchMap((dialogResponse) => {
          const analyticsEvent = new AnalyticsEvent(ActionEnum.CREATE, EntityTypeEnum.DASHBOARD);

          if (dialogResponse === 'yes') {
            analyticsEvent.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.SAVE});
            const dataObject = dialogRef.componentInstance.getGeneratedObject();
            this.analyticsService.trackEvent(analyticsEvent);
            return this.createDashboard(dataObject.dashboardName);
          } else {
            analyticsEvent.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
            this.analyticsService.trackEvent(analyticsEvent);
            return EMPTY;
          }
        }))
      .subscribe(() => {this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.DASHBOARD_ADDED'));});
  }

  /**
   * Open dialog to delete a dashboard.
   * @param dashboard Dashboard to delete.
   */
  public openDeleteDashboardDialog(dashboard: Dashboard): Observable<any> {
    const dialogRef = this.popupManager.showOkCancelPopup({
      dialogTitle: this.translate.instant('TITLE.DELETE_DASHBOARD'),
      dialogMessage: this.translate.instant('MESSAGE.DELETE_DASHBOARD'),
      okText: this.translate.instant('BUTTON.DELETE'),
      type: 'warning'
    });
    return dialogRef.afterClosed().pipe(
      switchMap(dialogResponse => {
        const analyticsEvent = new AnalyticsEvent(ActionEnum.DELETE, EntityTypeEnum.DASHBOARD);
        if (dialogResponse === 'yes') {
          analyticsEvent.addProperties({
            [AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.SAVE,
            [AnalyticsKeyEnum.ENTITY_ID]: dashboard.id
          });
          this.analyticsService.trackEvent(analyticsEvent);
          return this.deleteDashboard(dashboard);
        } else {
          analyticsEvent.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
          this.analyticsService.trackEvent(analyticsEvent);
          return EMPTY;
        }
      })
    );
  }

  /**
   * Make an API request to create new Dashboard with the provided data by copying the current one.
   * @param dashboardName Name of the new dashboard.
   * @return Observable emitting the new Dashboard that was created.
   */
  public createDashboard(dashboardName: string): Observable<Dashboard> {
    const copyDashboard = this.copyCurrentDashboard();
    const mutation = gql`
      mutation CreateDashboard($orgId: String!, $entityType: EntityType!, $dashboardInput: DashboardInput!){
        createDashboard(organizationId: $orgId, entityType: $entityType, createDashboardInput: $dashboardInput) {
          ...DashboardInfo
        }
      }
      ${this.dashboardGraphqlFragment}
    `;
    const variables = {
      orgId: this.appManager.currentOrganization.id,
      entityType: copyDashboard.entityType,
      dashboardInput: {
        name: dashboardName,
        charts:
          copyDashboard.charts.map(chart => {
            return {
              options: {},
              config: chart.config
            };
          })
      }
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Dashboard, data['createDashboard'])),
        tap(newDashboard => {
            this.applyDashboardSubject.next(newDashboard);
          }
        )
      );
  }

  /**
   * Make an API request to delete a Dashboard.
   * @param dashboard Dashboard to delete.
   */
  public deleteDashboard(dashboard: Dashboard): Observable<any> {
    const mutation = gql`
      mutation DeleteDashboard($id: String!) {
        deleteDashboard(dashboardId: $id)
      }
    `;
    const variables = {
      id: dashboard.id
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(tap(() => this.deleteDashboardSubject.next(this.currentDashboard)));
  }

  /**
   * Build the chart tooltip template for a series type
   * @param params Series tooltip renderer params containing names and values to show in tooltip
   * @param options Chart options which might contain suffixes or formatTypes
   * @param seriesType Type of the chart series
   * @return {string} Formatted tooltip result
   * @private
   */
  private buildTooltipTemplate(params: any, options: ChartOptions, seriesType: ChartType): string {
    const polarSeriesType: ChartType[] = ['donut', 'pie'];
    const undefinedValue = this.translate.instant('LABEL.UNDEFINED_CHART_CATEGORY');
    const tooltipTemplate = (xLabel, xValue, yLabel, yValue): string => {
      xValue = (xValue === ' ') ? undefinedValue : xValue;
      yValue = (yValue === ' ') ? undefinedValue : yValue;
      return '<b>' + xLabel + ':</b> ' + xValue + '<br/>' + '<b>' + yLabel + ':</b> ' + yValue;
    };

    return polarSeriesType.includes(seriesType) ?
      //Pie series tooltip template
      tooltipTemplate(
        params.calloutLabelName,
        params.datum[params.calloutLabelKey] ?? ' ',
        params.angleName,
        [<string>this.fieldFormatPipe.transform(params.datum[params.angleKey], options?.formatTypes?.y), options?.suffix?.y].join(' ')
      ) :
      //Cartesian series tooltip template
      tooltipTemplate(
        params.xName,
        [<string>this.fieldFormatPipe.transform(params.datum[params.xKey], options?.formatTypes?.x), options?.suffix?.x].join(' '),
        params.yName,
        [<string>this.fieldFormatPipe.transform(params.datum[params.yKey], options?.formatTypes?.y), options?.suffix?.y].join(' ')
      );
  }

  /**
   * Generate functions to format axes and tooltip values with charts' options such as suffixes
   * @param chart Chart to update by adding formatters
   * @return {Chart} Chart with updated theme overrides options
   */
  public addFormatterOptions(chart: Chart): Chart {
    const cartesianSeriesType: ChartType[] = ['line', 'scatter', 'bubble', 'area', 'bar', 'histogram'];
    const polarSeriesType: ChartType[] = ['donut', 'pie'];
    const chartThemeOverrides = chart.config.chartThemeOverrides || {};

    // Build tooltip renderer
    for (const type of cartesianSeriesType.concat(polarSeriesType)) {
      const tooltipRenderer = (params: AgSeriesTooltipRendererParams<any>): AgTooltipRendererResult => {
        return {content: this.buildTooltipTemplate(params, chart.options, type)};
      };
      const tooltipRendererPath = [type, 'series', 'tooltip', 'renderer'];
      setValue(chartThemeOverrides, tooltipRendererPath, tooltipRenderer);
    }

    //Build label formatter for all charts
    const labelFormatter = (params: AgChartLabelFormatterParams<any>): string => {
      if (Number.isNaN(params.value)) return params.value;
      const number = this.fieldFormatPipe.transform(params.value, chart.options?.formatTypes?.y);
      return number !== '' ? [number, chart.options?.suffix?.y].join(' ') :
        this.translate.instant('LABEL.UNDEFINED_CHART_CATEGORY');
    };

    for (const type of cartesianSeriesType) {
      setValue(chartThemeOverrides, [type, 'axes', 'number', 'label', 'formatter'], labelFormatter);
    }

    for (const type of polarSeriesType) {
      setValue(chartThemeOverrides, [type, 'series', 'sectorLabel', 'formatter'], labelFormatter);
      setValue(chartThemeOverrides, [type, 'series', 'calloutLabel', 'formatter'], labelFormatter);
    }

    return chart;
  }
}
